<template>
  <div class="main-wrapper">
    <div class="pannel-wrapper">
      <div class="solar-wrapper">
        <div class="solar-wrapper-header">
          <img
            v-if="!anonymousUser"
            src="@/assets/icons/newEyeOpenIcon.svg"
            alt="eye"
            class="eye"
            @click="changeShowFlag"
            v-show="areasAreShown"
          />
          <img
            v-if="!anonymousUser"
            src="@/assets/icons/newEyeClosedIcon.svg"
            alt="eye"
            class="eye-closed"
            @click="changeShowFlag"
            v-show="!areasAreShown"
          />
          <div
            class="section-header-right-side"
            @click="toggleSolars"
            :class="{ inactiveGray: !areasAreShown }"
          >
            <p class="cluster-title">{{ $t("sidebar.solar") }}</p>
            <img
              src="@/assets/icons/newArrowUp.svg"
              alt="triangle"
              class="arrow"
              v-show="showSolars"
            />
            <img
              src="@/assets/icons/newArrowDown.svg"
              alt="triangle"
              class="arrow"
              v-show="!showSolars"
            />
          </div>
        </div>
        <div v-if="showSolars" class="solar-details-box">
          <div
            class="solar-inside-wrapper"
            v-for="(solar, index) in completedAreas"
            :key="index"
          >
            <div
              class="solar-header"
              :class="{
                active: index == selectedSolar && solar.show,
                grayClass: !areasAreShown || !solar.show,
                expand: index == selectedSolar,
              }"
            >
              <template v-if="editingIndex !== index">
                <img
                  src="@/assets/icons/newEyeOpenIcon.svg"
                  alt="eye"
                  class="eye item-eye"
                  @click="hideThisSolarArea(solar, index)"
                  v-if="solar.show && !anonymousUser"
                />
                <img
                  src="@/assets/icons/newEyeClosedIcon.svg"
                  alt="eye"
                  class="eye-closed item-eye"
                  @click="showThisSolarArea(solar, index)"
                  v-if="!solar.show && !anonymousUser"
                />
                <div
                  @click="showSolarDetails(solar, index)"
                  class="left-text"
                >
                  <p>
                    {{ solar.name || `${$t("sidebar.pvArray")} ${index + 1}` }}
                  </p>
                </div>
                <div
                  class="anotation-image-wrapper"
                  :class="{
                    'shown-solars': solar.show && areasAreShown && !anonymousUser,
                  }"
                >
                  <img
                    src="@/assets/icons/edit-panel-dark.svg"
                    alt="edit"
                    class="edit-icon"
                    v-if="showTrashIcon && solar.show && !anonymousUser && featureFlag('solar_object_renaming')"
                    @mousedown.prevent
                    @click="startEditing(solar, index)"
                  />
                  <img
                    src="@/assets/icons/newTrashIcon.svg"
                    alt="trash"
                    class="trash"
                    v-if="showTrashIcon && solar.show && !anonymousUser"
                    @click="openDeleteModal(solar)"
                  />
                </div>
              </template>
              <div v-else class="edit-name-wrapper">
                <textarea
                  class="edit-name-input"
                  :placeholder="`${$t('sidebar.pvArray')} ${index + 1}`"
                  v-model="editNameValue"
                  rows="1"
                  @keydown.stop
                  @keydown.enter.exact.prevent
                  @keyup.stop
                  @keyup.enter="confirmRename(solar, index)"
                  @keyup.esc="cancelRename"
                  @blur="confirmRename(solar, index)"
                  @input="autoResize"
                  ref="editInput"
                ></textarea>
              </div>
            </div>
            <div class="solar-details" v-if="index == selectedSolar">
              <div class="details-text">
                <p>{{ $t("sidebar.outlineArea") }}</p>
                <span>{{ $n(Number(calculateSurfaceArea(solar)), "decimal") }}m²</span>
              </div>
              <div class="details-text">
                <p>{{ $t("sidebar.amountofpanels") }}</p>
                <span>{{ getAreaPanelsCount(solar) }}</span>
              </div>
              <div class="details-text">
                <p>{{ $t("sidebar.panelsArea") }}</p>
                <span> {{ $n(calculateVisiblePanelsArea(solar), "decimal") }}m² </span>
              </div>
              <div class="details-text">
                <p>{{ $t("sidebar.slope") }}</p>
                <span>{{ $n(solar.angle, "decimal") }}°</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
    <RemoveSolarGroupModal
      :isVisible="removeModalOpen"
      :areaName="isBobbieContext ? null : selectedAreaDisplayName"
      :areaIndex="isBobbieContext ? selectedAreaIndex : null"
      @confirm="confirmDeleteSolarGroup"
      @close-modal="closeRemoveModal"
      @update:isVisible="removeModalOpen = $event"
    />
  </div>
</template>

<script setup>
import { onMounted, ref, watch, nextTick, computed } from "vue";
import { useStore } from "vuex";
import { useI18n } from "vue-i18n";
import RemoveSolarGroupModal from "@/components/RightSidebar/RemoveSolarGroupModal.vue";

import API from "@/api/API";
import { hasPermission, Permission } from "@/constants/permissions";

const store = useStore();
const { t } = useI18n();
const isBobbieContext = computed(() => store.state.auth.isBobbieContext);
const featureFlag = (name) => store.getters["featureFlag"](name);

const props = defineProps([
  "completedAreas",
  "solarGroupDetails",
  "hideAreas",
  "showAreas",
  "hideSingleArea",
  "showSingleArea",
  "config",
  "setVisibilityInStore",
  "objectIsloaded",
  "removeSolarGroup",
  "unselectSolarArea",
  "anonymousUser",
  "getAreaPanelsCount",
  "calculateVisiblePanelsArea",
  "calculateSurfaceArea",
  "active",
  "removeSelectedArea",
]);
const emits = defineEmits(["updateSelectedArea", "setColapsedSections"]);

const areasAreShown = ref(
  !(
    props.solarGroupDetails.every((v) => v.show === true) ||
    props.solarGroupDetails.every((v) => v.show === false)
  )
);
const showTrashIcon = ref(true);
const editingIndex = ref(null);
const editNameValue = ref("");
const editInput = ref(null);

function startEditing(solar, index) {
  if (editingIndex.value !== null && editingIndex.value !== index) {
    confirmRename(props.completedAreas[editingIndex.value], editingIndex.value);
  }
  editingIndex.value = index;
  editNameValue.value = solar.name || "";
  nextTick(() => {
    if (editInput.value) {
      const input = Array.isArray(editInput.value) ? editInput.value[0] : editInput.value;
      if (input) {
        input.style.height = "auto";
        input.style.height = input.scrollHeight + "px";
        input.focus();
      }
    }
  });
}

function confirmRename(solar, index) {
  if (editingIndex.value !== index) return;

  const newName = editNameValue.value.trim();
  if (newName) {
    solar.name = newName;
    if (hasPermission(Permission.Update_Objects) && solar.id) {
      API.airteam3DViewer.patchSolarAreaObject({ name: newName }, solar.id);
    }
  } else if (solar.name && featureFlag('solar_object_renaming')) {
    solar.name = null;
    if (!props.sample && solar.id) {
      API.airteam3DViewer.patchSolarAreaObject({ name: null }, solar.id);
    }
  }
  editingIndex.value = null;
  editNameValue.value = "";
}

function cancelRename() {
  editingIndex.value = null;
  editNameValue.value = "";
}

function autoResize(event) {
  const el = event.target;
  el.style.height = "auto";
  el.style.height = el.scrollHeight + "px";
}

const changeShowFlag = () => {
  if (props.active === 2 && areasAreShown.value) {
    return;
  }

  const newValue = !areasAreShown.value;

  props.setVisibilityInStore("solar_groups_visible", newValue);

  if (newValue) {
    props.showAreas();
    showTrashIcon.value = true;
  } else {
    props.hideAreas();
    showTrashIcon.value = false;
    selectedSolar.value = null;
  }

  areasAreShown.value = newValue;
};

function hideThisSolarArea(solar, index) {
  if (areasAreShown.value) {
    props.hideSingleArea(solar, true);
  }
  if (selectedSolar.value == index) {
    selectedSolar.value = null;
  }
  setSolarAreaAppearance(solar.id, false);
  solar.show = false;
  props.unselectSolarArea(solar);
}

function showThisSolarArea(solar, index) {
  if (areasAreShown.value) {
    props.showSingleArea(solar);
  }
  setSolarAreaAppearance(solar.id, true);
  solar.show = true;
}

const showSolars = ref(false);
const selectedSolar = ref(null);

function toggleSolars() {
  if (props.solarGroupDetails.length) {
    showSolars.value = !showSolars.value;
  }
  if (showSolars.value) {
    emits("setColapsedSections", "solarComponent");
    selectedSolar.value = null;
  } else {
    props.unselectSolarArea();
  }
}
function colapseFromParent() {
  if (props.solarGroupDetails.length) {
    showSolars.value = false;
    if (selectedSolar.value != null) {
      props.unselectSolarArea();
    }
  }
}

function showSolarDetails(solar, number) {
  if (!solar.show || !areasAreShown.value) {
    return;
  }
  if (selectedSolar.value != number) {
    props.unselectSolarArea();

    emits("updateSelectedArea", solar);
    selectedSolar.value = number;
  } else {
    selectedSolar.value = null;
    props.unselectSolarArea();
  }
}

async function setSolarAreaAppearance(id, show) {
  if (!hasPermission(Permission.Update_Objects)) {
    return;
  }
  return await API.airteam3DViewer.patchObject(id, show);
}

function handleCreateSolarGroup() {
  areasAreShown.value = true;
  if (props.solarGroupDetails.length) {
    showSolars.value = true;
  }
  if (showSolars.value) {
    emits("setColapsedSections", "solarComponent");
    props.setVisibilityInStore("solar_groups_visible", showSolars.value);

    const lastSolarGroup = props.solarGroupDetails[props.solarGroupDetails.length - 1];
    showThisSolarArea(lastSolarGroup, props.solarGroupDetails.length - 1);
    showSolarDetails(lastSolarGroup, props.solarGroupDetails.length - 1);
    lastSolarGroup.firstTimeShown = true;

    setTimeout(() => {
      const selectedElement = document.querySelector(".solar-header.active.expand");
      if (selectedElement) {
        selectedElement.scrollIntoView({
          behavior: "smooth",
          block: "center",
          inline: "nearest",
        });
      }
      startEditing(lastSolarGroup, props.solarGroupDetails.length - 1);
    }, 100);
  }
}
function selectAreaOnSidebar(selectedSolarArea) {
  if (!showSolars.value) {
    toggleSolars();
  }
  let selectedSolarAreaIndex = props.solarGroupDetails.findIndex(
    (element) => element.plane.id === selectedSolarArea.plane.id
  );

  if (selectedSolar.value != selectedSolarAreaIndex) {
    selectedSolar.value = selectedSolarAreaIndex;

    setTimeout(() => {
      const selectedElement = document.querySelector(".solar-header.active.expand");
      if (selectedElement) {
        selectedElement.scrollIntoView({
          behavior: "smooth",
          block: "center",
          inline: "nearest",
        });
      }
    }, 100);
  } else {
    if (props.active == 6) {
      return;
    }

    selectedSolar.value = null;
    props.unselectSolarArea(true);
  }
}
function unselectSolarAreaOnSidebar() {
  selectedSolar.value = null;
}

defineExpose({
  colapseFromParent,
  handleCreateSolarGroup,
  selectAreaOnSidebar,
  unselectSolarAreaOnSidebar,
  showSolarFlag,
  editingIndex,
});

const removeModalOpen = ref(false);
const selectedAreaToDelete = ref(null);
const selectedAreaIndex = ref(null);
const selectedAreaDisplayName = computed(() => {
  const customName = selectedAreaToDelete.value?.name?.trim();
  return customName || `${t("sidebar.pvArray")} ${selectedAreaIndex.value}`;
});

function openDeleteModal(solar) {
  selectedAreaToDelete.value = solar;
  selectedAreaIndex.value = props.completedAreas.indexOf(solar) + 1;
  removeModalOpen.value = true;
}

function closeRemoveModal() {
  removeModalOpen.value = false;
  selectedAreaToDelete.value = null;
  selectedAreaIndex.value = null;
}

function confirmDeleteSolarGroup() {
  if (selectedAreaToDelete.value) {
    handleDeleteSolarGroup(selectedAreaToDelete.value);
  }
  closeRemoveModal();
}

function handleDeleteSolarGroup(solar) {
  if (solar.type === "SOLAR_GROUP") {
    props.removeSolarGroup(solar);
  } else if (solar.type === "AREA") {
    props.removeSelectedArea(solar);
  } else {
    console.error("Unknown solar area type:", solar.type);
  }

  if (props.active === 2 || props.active === 3 || props.active === 6)
    props.unselectSolarArea();
}

function showSolarFlag() {
  if (props.solarGroupDetails.length) {
    props.setVisibilityInStore("solar_groups_visible", true);

    areasAreShown.value = true;
  }
}

// Watch for changes in props.areaDetails
watch(
  () => props.solarGroupDetails.length,
  (newLength) => {
    if (newLength > 0) {
      showSolars.value = true;
    }
  }
);

watch(
  () => props.objectIsloaded,

  (newValue) => {
    if (newValue) {
      if (props.config.solar_groups_visible === false) {
        // If solar_groups_visible is false in config, hide everything
        areasAreShown.value = false;
        props.hideAreas();
        showTrashIcon.value = false;
      } else {
        // If solar_groups_visible is true, show areas based on their individual states
        areasAreShown.value = true;

        showTrashIcon.value = true;
      }
    }
  }
);
</script>

<style lang="scss" scoped>
.main-wrapper {
  .pannel-wrapper {
    max-height: 31rem;
    overflow-y: scroll;
    overflow-x: hidden;
    -ms-overflow-style: none;
    scrollbar-width: none;
    .solar-wrapper {
      .solar-wrapper-header {
        position: sticky;
        top: 0;
        z-index: 1;
        background: var(--color-dark-bg-secondary);
        max-height: 30px;
        display: flex;
        gap: 1rem;
        align-items: center;
        padding: 1rem 0;
        .section-header-right-side {
          cursor: pointer;
          display: flex;
          justify-content: space-between;
          width: 100%;
          align-items: center;
        }
        .section-header-right-side.inactiveGray {
          p {
            color: gray;
          }
          img {
            filter: grayscale(1) brightness(0.5); /* Initial gray color */
          }
        }
        p {
          font-family: "Roboto";
          font-size: 14px;
          font-style: normal;
          font-weight: bold;
          line-height: 19px;
          color: white;
          letter-spacing: 0.03em;
        }
        img {
          height: 12px;
          width: 18px;
          cursor: pointer;
        }
        .trash,
        .arrow {
          height: 20px;
          width: 20px;
        }
      }
      .solar-details-box {
        display: flex;
        flex-direction: column;
        gap: 1rem;
        margin-bottom: 12px;
        margin-top: 6px;

        .solar-inside-wrapper {
          .solar-header.active.expand {
            border: 1px solid var(--color-gree300);
            border-radius: 10px 10px 0 0;
            border-bottom: none;
            margin-bottom: 1px;
            .anotation-image-wrapper {
              display: flex;
            }
          }
          .solar-header.active:hover {
            .anotation-image-wrapper {
              img {
                filter: brightness(0) invert(1); /* White color on hover */
              }
            }
          }
          .solar-header.expand {
            border: none;
            margin-bottom: 1px;
            border-radius: 10px 10px 0 0;
          }
          .solar-header:not(.active):hover {
            .anotation-image-wrapper.shown-solars {
              display: flex;
              .trash,
              .edit-icon {
                filter: grayscale(1) brightness(1.7);
              }
              img:hover {
                filter: brightness(0) invert(1); /* White color on hover */
              }
            }
          }
          .solar-header {
            background: var(--color-dark-bg-primary);
            border: 1px solid var(--color-dark-bg-tertiary);
            display: flex;
            gap: 8px;
            align-items: center;
            padding: 1rem 0.5rem;
            max-height: 30px;
            border-radius: 10px;
            display: flex;
            justify-content: space-between;
            &:has(.edit-name-wrapper) {
              max-height: none;
            }

            .left-text {
              flex: 1;
              width: 0;
              min-width: 0;
              overflow: hidden;
              p {
                white-space: nowrap;
                overflow: hidden;
                text-overflow: ellipsis;
              }
            }
            .edit-name-wrapper {
              flex: 1;
              min-width: 0;
              overflow: hidden;
              .edit-name-input {
                width: 100%;
                box-sizing: border-box;
                background: #b6b6b61a;
                border: none;
                border-radius: 4px;
                color: white;
                font-family: "Roboto";
                font-size: 14px;
                padding: 2px 6px;
                outline: none;
                resize: none;
                overflow: hidden;
                overflow-wrap: break-word;
                word-break: break-word;
                line-height: 19px;
                &::placeholder {
                  color: gray;
                }
              }
            }

            p.grayCustome {
              color: gray;
            }
            .anotation-image-wrapper {
              display: none;
              gap: 8px;
            }
            .edit-icon {
              margin-top: 2px;
            }
            .item-eye {
              width: 16px;
              height: 16px;
              cursor: pointer;
              flex-shrink: 0;
            }

            p {
              cursor: pointer;
              color: white;
            }
            img {
              width: 16px;
              height: 16px;
              cursor: pointer;
              filter: brightness(0) invert(1);
            }
          }
          .solar-details {
            background: var(--color-dark-bg-primary);
            border: 1px solid var(--color-gree300);
            border-top: none;
            border-bottom-left-radius: 10px;
            border-bottom-right-radius: 10px;
            padding: 5px 5px 8px 10px;
            .details-text {
              display: flex;
              justify-content: space-between;
            }
          }

          .solar-header.grayClass {
            margin-bottom: 1px;
            p {
              color: gray;
            }
            .item-eye {
              filter: grayscale(1) brightness(0.7);
            }
            .item-eye.eye-closed {
              filter: grayscale(1) brightness(1.3);
            }
            .item-eye:hover {
              filter: brightness(0) invert(1);
            }
          }
          .solar-header.active.grayClass {
            border: none;
            .item-eye {
              filter: brightness(0) invert(0.5);
            }
            .item-eye:hover {
              filter: brightness(0) invert(1);
            }
          }
          .solar-details.grayClass {
            border: none;
            p {
              color: gray;
            }
            span {
              color: gray;
            }
          }
        }
      }
    }
  }
}

.eye:hover {
  filter: brightness(0) invert(1); /* White color on hover */
}
.eye-closed {
  filter: grayscale(1) brightness(1);
}

@media (max-width: 1600px) {
  .main-wrapper {
    .pannel-wrapper {
      .solar-wrapper {
        .solar-details-box {
          display: flex;
          flex-direction: column;
          gap: 1rem;
          margin-bottom: 12px;
          margin-top: 6px;

          .solar-inside-wrapper {
            .solar-header.active {
              border: none;
              border-top-left-radius: 10px;
              border-top-right-radius: 10px;
              border-bottom-right-radius: 0;
              border-bottom-left-radius: 0;
              img {
                width: 18px;
                height: 18px;
                cursor: pointer;
              }
            }
            .solar-details {
              p {
                font-size: 12px;
              }
            }
          }
        }
      }
    }
  }
}
</style>
